import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { formatDateYYYYMMDD } from '../../../common/dateUtils'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import { Button } from '../../Button/Button'
import { Tooltip } from '../../Tooltip/Tooltip'
import { IntegrationIcon } from '../../IntegrationIcon'
import { ReactComponent as LeafIcon } from '../../../assets/images/external-contact-update-leaf-field-icon.svg'
import { ReactComponent as ZocksIcon } from '../../../assets/ZocksIcon.svg'
import { IntegrationReferenceCurrentValue } from './IntegrationReferenceCurrentValue'
import { FieldInput } from './FieldInput'
import { hasFieldValue } from './utils'
import './IntegrationFieldReview.scss'

export type IntegrationFieldValue = string | number | boolean | string[]

export interface IntegrationReviewField {
  apiName: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'date' | 'reference' | 'boolean' | 'multipicklist'
  options?: string[]
  referenceObjectName?: string
}

export interface IntegrationFieldReviewProps {
  serviceId: IntegrationServiceId
  fields: IntegrationReviewField[]
  values: Record<string, IntegrationFieldValue>
  originalValues: Record<string, IntegrationFieldValue>
  selectedKeys: Set<string>
  onToggleField: (apiName: string) => void
  onToggleAll: (checked: boolean) => void
  onEditValue: (apiName: string, value: IntegrationFieldValue) => void
  groupLabel: string
  mode?: 'create' | 'update'
  currentValues?: Record<string, IntegrationFieldValue>
  buildEntityRoutingKey: (kind: 'list' | 'single', referenceObjectName: string) => string
}

export const IntegrationFieldReview: React.FC<IntegrationFieldReviewProps> = ({
  serviceId,
  fields,
  values,
  originalValues,
  selectedKeys,
  onToggleField,
  onToggleAll,
  onEditValue,
  groupLabel,
  mode = 'create',
  currentValues = {},
  buildEntityRoutingKey,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const integrationName = getIntegrationDisplayName(serviceId)
  const selectableFields = fields.filter((f) => hasFieldValue(values[f.apiName]))
  const allSelected = selectableFields.length > 0 && selectableFields.every((f) => selectedKeys.has(f.apiName))
  const isCreateMode = mode === 'create'
  const gridModifier = isCreateMode ? 'is-create-mode' : ''

  return (
    <div className='contact-update-container zocks-bg-color-white zocks-border-0.25 zocks-border-gray-300 border d-flex flex-column h-100'>
      <div className='flex-grow-1 overflow-auto'>
        <div className={`contact-update-grid header-row ${gridModifier} flex-shrink-0`}>
          <div className='d-flex align-items-center zocks-gap-0.5'>
            <Form.Check type='checkbox' className='d-flex contact-information-checkbox' checked={allSelected} onChange={(e) => onToggleAll(e.target.checked)} />
            <span className='zocks-text-gray-700 fw-bold small'>CATEGORY</span>
            <button
              type='button'
              className='btn p-0 border-0 bg-transparent d-flex align-items-center zocks-text-gray-600'
              title={collapsed ? 'Expand all' : 'Collapse all'}
              onClick={() => setCollapsed((c) => !c)}
            >
              <i className='bi bi-arrows-collapse' />
            </button>
          </div>

          {!isCreateMode ? (
            <div className='d-flex align-items-center zocks-gap-0.25 zocks-p-0.5'>
              <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
              <span className='zocks-text-gray-700 fw-bold small'>CURRENT DATA</span>
              <Tooltip
                placement='top'
                title={`Data in this column reflects what is currently stored in your ${integrationName}. 'Not set' indicates this field is currently empty in ${integrationName}.`}
              >
                <i className='bi bi-info-square zocks-text-gray-700 ms-1' />
              </Tooltip>
            </div>
          ) : null}

          <div className='d-flex align-items-center zocks-gap-0.25 zocks-p-0.5'>
            <ZocksIcon width='1rem' height='1rem' fill='black' />
            <span className='zocks-text-gray-700 fw-bold small'>DATA FROM MEETING</span>
            <Tooltip
              placement='top'
              title={`Data in this column reflects what Zocks identified and will be pushed to ${integrationName} when you click the Update button.`}
            >
              <i className='bi bi-info-square zocks-text-gray-700 ms-1' />
            </Tooltip>
          </div>
        </div>

        <div className={`contact-update-grid data-row accordion-toggle ${gridModifier}`} onClick={() => setCollapsed((c) => !c)}>
          <div className='d-flex align-items-center zocks-gap-0.5'>
            <div onClick={(e) => e.stopPropagation()}>
              <Form.Check
                type='checkbox'
                className='d-flex contact-information-checkbox'
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </div>
            <span className='fw-bold'>{groupLabel}</span>
            <i className={`bi ${collapsed ? 'bi-caret-right-fill' : 'bi-caret-down-fill'} ms-2 zocks-text-secondary`} />
          </div>
          {!isCreateMode ? <div /> : null}
          <div />
        </div>

        {!collapsed &&
          fields.map((field) => {
            const value = values[field.apiName]
            const suggestion = hasFieldValue(value)
            const selected = selectedKeys.has(field.apiName)
            const original = originalValues[field.apiName]
            const edited = suggestion && JSON.stringify(value) !== JSON.stringify(original)
            const currentValue = currentValues[field.apiName]

            return (
              <div key={field.apiName} className={`contact-update-grid data-row ${gridModifier} ${field.type === 'textarea' ? 'textarea-row' : ''}`}>
                <div className='d-flex align-items-center zocks-gap-0.5'>
                  <Form.Check
                    type='checkbox'
                    className='d-flex contact-information-checkbox'
                    checked={selected}
                    onChange={() => onToggleField(field.apiName)}
                  />
                  <div className='d-flex align-items-center indent-1'>
                    <LeafIcon className='hierarchy-connector' />
                    <span className='zocks-text-secondary text-content'>{field.label}</span>
                  </div>
                </div>

                {!isCreateMode ? (
                  field.type === 'textarea' ? (
                    <div className='d-flex zocks-text-secondary current-data-textarea'>
                      {hasFieldValue(currentValue) ? <span>{currentValue}</span> : <span className='fst-italic zocks-text-gray-500'>Not set</span>}
                    </div>
                  ) : field.type === 'reference' ? (
                    <div className='d-flex align-items-center zocks-text-secondary'>
                      {hasFieldValue(currentValue) ? (
                        <IntegrationReferenceCurrentValue
                          serviceId={serviceId}
                          referenceObjectName={field.referenceObjectName}
                          value={currentValue}
                          buildEntityRoutingKey={buildEntityRoutingKey}
                        />
                      ) : (
                        <span className='fst-italic zocks-text-gray-500'>Not set</span>
                      )}
                    </div>
                  ) : field.type === 'boolean' ? (
                    <div className='d-flex align-items-center zocks-text-secondary'>
                      {hasFieldValue(currentValue) ? (
                        <Form.Check
                          className='d-flex contact-information-checkbox'
                          type='checkbox'
                          aria-label={`Current ${field.label}`}
                          checked={currentValue === true}
                          disabled
                          readOnly
                        />
                      ) : (
                        <span className='fst-italic zocks-text-gray-500'>Not set</span>
                      )}
                    </div>
                  ) : (
                    <div className='d-flex align-items-center zocks-text-secondary'>
                      {hasFieldValue(currentValue) ? (
                        <span className='current-data-text'>
                          {field.type === 'date'
                            ? formatDateYYYYMMDD(String(currentValue))
                            : field.type === 'multipicklist'
                              ? String(currentValue).split(';').join(', ')
                              : String(currentValue)}
                        </span>
                      ) : (
                        <span className='fst-italic zocks-text-gray-500'>Not set</span>
                      )}
                    </div>
                  )
                ) : null}

                <div className='d-flex align-items-center w-100 ps-2'>
                  <div className='d-flex flex-column w-100'>
                    <div className='d-flex align-items-center w-100 zocks-gap-0.5'>
                      <div className='widget-input-wrapper'>
                        <FieldInput field={field} value={value} onEditValue={onEditValue} serviceId={serviceId} buildEntityRoutingKey={buildEntityRoutingKey} />
                      </div>
                      {edited ? (
                        <Button
                          className='p-0 zocks-text-secondary d-flex align-items-center justify-content-center btn-reset'
                          onClick={() => onEditValue(field.apiName, original)}
                          title='Reset to generated value'
                        >
                          <i className='bi bi-arrow-clockwise' />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}
